// Manager — "Show me the policy" (User Journeys & Service Design v2, Journey
// 00 / PO Spec §1.1). The Maeve-First entry's policy card deep-links here —
// a focused, read-only view of the org's bereavement leave + pay
// entitlements for this case, sourced the same way the Employee portal's
// "Leave and entitlements" panel is (`GET /api/cases/:id/entitlements`,
// same RAG-grounded, policy-gate-safe summary — see `EntitlementsSummary`).
//
// Deliberately not a policy document browser/editor — that's the HR-only
// `/hr/policies` surface. This is the manager-facing plain-English read.
//
// Single query: the entitlements response also carries `employee`/
// `deceasedRelationship` (the only two fields this page needs beyond the
// summary itself), so there is no separate `GET /:id` fetch — that endpoint
// does three more DB reads (touchpoints, RTW plan, preferences) this page
// has no use for, and calling both would run the same manager/case
// authorization lookup twice per page view.

import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { DeceasedRelationship, EmployeeEntitlements } from '@besideu/shared/cases';
import { api, ApiError } from '@/lib/api.ts';
import { CaseNotFound } from '@/components/CaseNotFound.tsx';
import { EntitlementsSummary } from '@/components/EntitlementsSummary.tsx';

interface CaseEntitlementsResponse {
  entitlements: EmployeeEntitlements | null;
  employee: { id: string; name: string };
  deceasedRelationship: DeceasedRelationship;
}

export function ManagerCasePolicy() {
  const { id = '' } = useParams<{ id: string }>();

  const entitlements = useQuery({
    queryKey: ['cases', id, 'entitlements'],
    queryFn: () => api.get<CaseEntitlementsResponse>(`/api/cases/${id}/entitlements`),
    enabled: id !== '',
  });

  if (entitlements.isPending) return <p className="text-muted-foreground">Loading…</p>;
  if (entitlements.error) {
    if (entitlements.error instanceof ApiError && entitlements.error.status === 404) {
      return (
        <section className="mx-auto max-w-2xl space-y-6">
          <CaseNotFound
            backTo="/manager/team"
            reason="It may belong to another manager, or it may have been removed."
          />
        </section>
      );
    }
    return <p className="text-traffic-red">Could not load this support pathway.</p>;
  }

  const { employee, deceasedRelationship } = entitlements.data;

  return (
    <section className="mx-auto max-w-2xl space-y-6">
      <Link
        to={`/manager/cases/${id}`}
        className="text-sm text-muted-foreground underline-offset-4 hover:underline"
      >
        ← Back to the support pathway
      </Link>

      <div>
        <h1 className="font-heading text-xl font-bold">Bereavement leave and pay</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {employee.name} — {deceasedRelationship}
        </p>
      </div>

      <EntitlementsSummary
        title="Entitlements"
        placeholder="Specific leave and pay details will appear here once your organisation's policy documents are connected. Until then, HR can answer questions."
        entitlements={entitlements.data.entitlements}
      />
    </section>
  );
}
